import {
  Button, Grid, Typography, Box, Divider, Paper,
  TextField,
  FormControlLabel,
  Checkbox,
  FormGroup,
  Fab,
  CircularProgress,
} from '@material-ui/core';
import { Alert, Autocomplete, Skeleton } from '@material-ui/lab';
import type { AxiosError } from 'axios';
import axios from 'axios';
import * as React from 'react';
import useSWR from 'swr';
import { useSnackbar } from 'notistack';
import {
  useParams, useNavigate,
} from 'react-router-dom';

import getFacilities from '../../../lib/SWRfetchers/getFacilities';
import getDepartments from '../../../lib/SWRfetchers/getDepartments';
import getStations from '../../../lib/SWRfetchers/getStations';
import getEmployeeLevels from '../../../lib/SWRfetchers/getEmployeeLevels';
import GetSkillLevels from '../../../lib/SWRfetchers/getEmployeeSkillLevels';
import ChipSelect from '../../ChipSelect';
import type ILabelEnumPair from '../../global/GlobalInterfaces';
import UserRepairs from './UserRepairs';
import type { ILabelValuePair } from '../../marking/MarkingInterfaces';
import getNonCourierBinNames from '../../../lib/SWRfetchers/getNonCourierBinNames';

interface GetEmployeeDto {
  employeeId:string;
  firstName: string;
  lastName: string;
  email: string;
  barcode: string;
  phone: string;
  payrollId: string;
  isActive: boolean;
  isLocked: boolean;
  facilities: number[];
  employeeLevels: number[];
  departments: number[];
  jobFunctions: number[];
  assignedRepairs: number;
  assignedLocation: string;
  skillLevelTypes: Array<ILabelValuePair>;
}

const UserUpsert: React.FC = () => {
  const { facilities } = getFacilities();
  const { departments } = getDepartments();
  const { stations } = getStations();
  const { binNames } = getNonCourierBinNames();
  const { skillLevels } = GetSkillLevels();

  const { employeeLevels } = getEmployeeLevels();

  const { enqueueSnackbar } = useSnackbar();

  const { userId: userIdUrlParam } = useParams();
  const navigate = useNavigate();

  const [checkboxState, setCheckboxState] = React.useState({
    isActive: true,
    isLocked: false,
  });

  const [formValues, setFormValues] = React.useState({
    firstName: '',
    lastName: '',
    email: '',
    barcode: '',
    password: '',
    phoneNumber: '',
    pin: '',
    payrollId: '',
    assignedLocation: '',
  });

  const [showErrors, setShowErrors] = React.useState<boolean>(false);
  const [savingUser, setSavingUser] = React.useState<boolean>(false);
  const [facilitiesSelected, setFacilitiesSelected] = React.useState<number[]>([]);
  const [departmentSelected, setDepartmentsSelected] = React.useState<number[]>([]);
  const [stationsSelected, setStationsSelected] = React.useState<number[]>([]);
  const [skillLevelsSelected, setSkillLevelsSelected] = React.useState<ILabelValuePair[]>([]);

  const [roleSelected, setRoleSelected] = React.useState<number[]>([]);

  const [showRepairsModal, setShowRepairsModal] = React.useState<boolean>(false);
  const [repairsAssigned, setRepairsAssigned] = React.useState<number>(0);

  const handleChipFacilitiesSelect = (selectedType: ILabelEnumPair, type:'facilities' | 'departments' | 'roles' | 'stations') => {
    if (type === 'facilities') {
      if (facilitiesSelected.findIndex((x) => x === selectedType.value) === -1) {
        setFacilitiesSelected([...facilitiesSelected, selectedType.value]);
      } else {
        const newFacilitiesSelected = facilitiesSelected;
        newFacilitiesSelected.splice(newFacilitiesSelected.indexOf(selectedType.value), 1);
        setFacilitiesSelected([...newFacilitiesSelected]);
      }
    }
    if (type === 'departments') {
      if (departmentSelected.findIndex((x) => x === selectedType.value) === -1) {
        setDepartmentsSelected([...departmentSelected, selectedType.value]);
      } else {
        const newDepartmentSelected = departmentSelected;
        newDepartmentSelected.splice(newDepartmentSelected.indexOf(selectedType.value), 1);
        setDepartmentsSelected([...newDepartmentSelected]);
      }
    }
    if (type === 'roles') {
      if (roleSelected.findIndex((x) => x === selectedType.value) === -1) {
        setRoleSelected([...roleSelected, selectedType.value]);
      } else {
        const newRoleSelected = roleSelected;
        newRoleSelected.splice(newRoleSelected.indexOf(selectedType.value), 1);
        setRoleSelected([...newRoleSelected]);
      }
    }
    if (type === 'stations') {
      if (stationsSelected.findIndex((x) => x === selectedType.value) === -1) {
        setStationsSelected([...stationsSelected, selectedType.value]);
      } else {
        const newStationsSelected = stationsSelected;
        newStationsSelected.splice(newStationsSelected.indexOf(selectedType.value), 1);
        setStationsSelected([...newStationsSelected]);
      }
    }
  };

  const userFetcher = (url: string, userIdParam: string) => axios.get<GetEmployeeDto>(url, {
    params: {
      userId: userIdParam,
    },
  })
    .then((response) => {
      setRoleSelected([...response.data.employeeLevels]);
      setDepartmentsSelected([...response.data.departments]);
      setFacilitiesSelected([...response.data.facilities]);
      setStationsSelected([...response.data.jobFunctions]);
      setFormValues({
        ...formValues,
        firstName: response.data.firstName,
        lastName: response.data.lastName,
        email: response.data.email,
        barcode: response.data.barcode,
        phoneNumber: response.data.phone,
        payrollId: response.data.payrollId,
        assignedLocation: response.data.assignedLocation,
      });
      setCheckboxState({
        isActive: response.data.isActive,
        isLocked: response.data.isLocked,
      });
      setRepairsAssigned(response.data.assignedRepairs);
      setSkillLevelsSelected([...response.data.skillLevelTypes]);
      return response.data;
    })
    .catch((e: AxiosError) => {
      enqueueSnackbar(`Redirecting to view users page, there was the following error when fetching the user: ${JSON.stringify(e?.response?.data)}`, { persist: false, variant: 'error' });
      navigate('/view-users');
    });

  const { data } = useSWR(
    userIdUrlParam
      ? ['/EmployeeInfo/GetEmployee', userIdUrlParam]
      : null,
    (url, barcodeParam) => userFetcher(url, barcodeParam), {
      revalidateOnFocus: false,
      revalidateOnMount: true,
      revalidateOnReconnect: false,
      refreshWhenOffline: false,
      refreshWhenHidden: false,
      refreshInterval: 0,
    },
  );

  const postValues = {
    ...formValues,
    ...checkboxState,
    EmployeeId: userIdUrlParam,
    Facilities: facilitiesSelected,
    EmployeeLevels: roleSelected,
    Departments: departmentSelected,
    JobFunctions: stationsSelected.filter((ss) => ss),
    SkillLevelTypes: [...skillLevelsSelected],
  };

  const saveNewEmployee = () => {
    if (
      postValues.firstName === ''
      || postValues.lastName === ''
      || postValues.email === ''
      || postValues.barcode === ''
      || !/^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[#?!@$%^&*-]).{8,}$/.test(formValues.password)
      || !/^\d{4}$/.test(formValues.pin)
    ) {
      enqueueSnackbar('Please fill out all required fields', { persist: false, variant: 'error' });
      setShowErrors(true);
      return;
    }
    if (facilitiesSelected.length === 0
      || roleSelected.length === 0
      || departmentSelected.length === 0) {
      enqueueSnackbar('Please select at least one facility, department, and role', { persist: false, variant: 'error' });
      setShowErrors(true);
      return;
    }

    setSavingUser(true);
    axios.post<{ userId:string }>('/EmployeeInfo/AddEmployee', { ...postValues })
      .then((response) => {
        enqueueSnackbar('User created successfully', { variant: 'success' });
        navigate(`/edit-user/${response.data.userId}`);
      })
      .catch((e: AxiosError) => {
        enqueueSnackbar(e?.response?.data, { persist: false, variant: 'error' });
      })
      .finally(() => {
        setSavingUser(false);
      });
  };

  const updateEmployee = () => {
    if (
      postValues.firstName === ''
      || postValues.lastName === ''
      || postValues.email === ''
      || postValues.barcode === ''
    ) {
      enqueueSnackbar('Please fill out all required fields', { persist: false, variant: 'error' });
      setShowErrors(true);
      return;
    }
    if (facilitiesSelected.length === 0
      || roleSelected.length === 0
      || departmentSelected.length === 0) {
      enqueueSnackbar('Please select at least one facility, department, and role', { persist: false, variant: 'error' });
      setShowErrors(true);
      return;
    }
    if (checkboxState.isActive === false && repairsAssigned > 0) {
      enqueueSnackbar('Cannot deactivate user with active assigned repairs', { persist: false, variant: 'error' });
      setShowRepairsModal(true);
      return;
    }
    setSavingUser(true);

    axios.post('/EmployeeInfo/UpdateEmployee', { ...postValues })
      .then(() => {
        enqueueSnackbar('User saved successfully', { variant: 'success' });
      })
      .catch((e: AxiosError) => {
        enqueueSnackbar(e?.response?.data, { persist: false, variant: 'error' });
      })
      .finally(() => {
        setSavingUser(false);
      });
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    switch (event.target.id) {
      case 'firstName':
        setFormValues({ ...formValues, firstName: event.target.value });
        break;
      case 'lastName':
        setFormValues({ ...formValues, lastName: event.target.value });
        break;
      case 'email':
        setFormValues({ ...formValues, email: event.target.value });
        break;
      case 'barcode':
        setFormValues({ ...formValues, barcode: event.target.value });
        break;
      case 'password':
        setFormValues({ ...formValues, password: event.target.value });
        break;
      case 'pin':
        setFormValues({ ...formValues, pin: event.target.value });
        break;
      case 'phoneNumber':
        setFormValues({ ...formValues, phoneNumber: event.target.value });
        break;
      case 'payrollId':
        setFormValues({ ...formValues, payrollId: event.target.value });
        break;
      default:
        break;
    }
  };

  return (
    <Grid container spacing={0}>
      <Grid item xs={9} container spacing={3} alignItems="stretch">
        <Grid item xs={12}>
          <Typography variant="h5">
            { userIdUrlParam ? 'Edit User' : 'Add New User'}
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Paper>
            <Grid container spacing={3} alignItems="stretch">
              <Grid item xs={10}>
                <Box pl={2} pt={1}>
                  <Typography variant="h5">{'User Details'}</Typography>
                </Box>
              </Grid>
              <Grid item xs={2}>
                {
                    userIdUrlParam && !data
                      ? (
                        <Skeleton
                          variant="circle"
                        >
                          <Fab
                            variant="extended"
                            size="medium"
                            color="primary"
                            aria-label="add"
                          >
                            {`Assigned Repairs: ${repairsAssigned || 0}`}
                          </Fab>
                        </Skeleton>
                      )
                      : (
                        <Fab
                          variant="extended"
                          size="medium"
                          color="primary"
                          aria-label="add"
                          onClick={() => setShowRepairsModal(true)}
                        >
                          {`Assigned Repairs: ${repairsAssigned || 0}`}
                        </Fab>
                      )
              }
              </Grid>
              <Grid item xs={12}>
                <Divider />
              </Grid>
              <Grid item xs={6}>
                <Box px={2}>
                  {
                    userIdUrlParam && !data
                      ? (
                        <Skeleton style={{ width: '100%' }}>
                          <TextField
                            fullWidth
                            variant="outlined"
                          />
                        </Skeleton>
                      )
                      : (
                        <TextField
                          onChange={handleInputChange}
                          value={formValues.firstName}
                          id="firstName"
                          fullWidth
                          label="First Name"
                          variant="outlined"
                          required
                          error={showErrors && formValues.firstName === ''}
                          helperText={showErrors && formValues.firstName === '' ? 'Required' : ''}
                        />
                      )
}
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Box px={2}>
                  {
                    userIdUrlParam && !data
                      ? (
                        <Skeleton style={{ width: '100%' }}>
                          <TextField
                            fullWidth
                            variant="outlined"
                          />
                        </Skeleton>
                      )
                      : (
                        <TextField
                          onChange={handleInputChange}
                          value={formValues.lastName}
                          id="lastName"
                          fullWidth
                          label="Last Name"
                          variant="outlined"
                          required
                          error={showErrors && formValues.lastName === ''}
                          helperText={showErrors && formValues.lastName === '' ? 'Required' : ''}
                        />
                      )
}
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Box pl={2} pt={1}>
                  <Typography variant="h5">{'Contact Information'}</Typography>
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Divider />
              </Grid>
              <Grid item xs={6}>
                <Box px={2}>
                  {
                    userIdUrlParam && !data
                      ? (
                        <Skeleton style={{ width: '100%' }}>
                          <TextField
                            fullWidth
                            variant="outlined"
                          />
                        </Skeleton>
                      )
                      : (
                        <TextField
                          onChange={handleInputChange}
                          value={formValues.email}
                          id="email"
                          fullWidth
                          label="Email"
                          variant="outlined"
                          required
                          autoComplete="off"
                          error={showErrors && formValues.email === ''}
                          helperText={showErrors && formValues.email === '' ? 'Required' : ''}
                        />
                      )
}
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Box px={2}>
                  {
                    userIdUrlParam && !data
                      ? (
                        <Skeleton style={{ width: '100%' }}>
                          <TextField
                            fullWidth
                            variant="outlined"
                          />
                        </Skeleton>
                      )
                      : (
                        <TextField
                          onChange={handleInputChange}
                          value={formValues.barcode}
                          id="barcode"
                          fullWidth
                          label="Barcode"
                          variant="outlined"
                          required
                          error={showErrors && formValues.barcode === ''}
                          helperText={showErrors && formValues.barcode === '' ? 'Required' : ''}
                        />
                      )
}
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Box px={2}>
                  {
                    userIdUrlParam && !data
                      ? (
                        <Skeleton style={{ width: '100%' }}>
                          <TextField
                            fullWidth
                            variant="outlined"
                          />
                        </Skeleton>
                      )
                      : (
                        <TextField
                          onChange={handleInputChange}
                          value={formValues.password}
                          id="password"
                          fullWidth
                          label="Password"
                          variant="outlined"
                          type="password"
                          autoComplete="new-password"
                          placeholder={userIdUrlParam ? '*****' : ''}
                          required={!userIdUrlParam}
                          error={!userIdUrlParam && showErrors && (formValues.password === '' || !/^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[#?!@$%^&*-]).{8,}$/.test(formValues.password))}
                          helperText={!userIdUrlParam
                            && showErrors
                            && formValues.password === ''
                            ? 'Required' : 'Must be at least 8 characters and contain the following: one lower letter, one uppercase letter, one number, and one symbol'}
                        />
                      )
}
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Box px={2}>
                  {
                    userIdUrlParam && !data
                      ? (
                        <Skeleton style={{ width: '100%' }}>
                          <TextField
                            fullWidth
                            variant="outlined"
                          />
                        </Skeleton>
                      )
                      : (
                        <TextField
                          onChange={handleInputChange}
                          value={formValues.pin}
                          id="pin"
                          fullWidth
                          label="Pin"
                          variant="outlined"
                          type="password"
                          autoComplete="new-pin"
                          placeholder={userIdUrlParam ? '*****' : ''}
                          required={!userIdUrlParam}
                          error={!userIdUrlParam && showErrors && (formValues.password === '' || !/^\d{4}$/.test(formValues.pin))}
                          helperText={!userIdUrlParam
                            && showErrors
                            && formValues.pin === ''
                            ? 'Required' : 'Must be at least 4 digits'}
                        />
                      )
}
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Box px={2}>
                  {
                    userIdUrlParam && !data
                      ? (
                        <Skeleton style={{ width: '100%' }}>
                          <TextField
                            fullWidth
                            variant="outlined"
                          />
                        </Skeleton>
                      )
                      : (
                        <TextField
                          onChange={handleInputChange}
                          value={formValues.phoneNumber}
                          id="phoneNumber"
                          fullWidth
                          label="Phone Number"
                          variant="outlined"
                        />
                      )
}
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Box px={2}>
                  {
                    userIdUrlParam && !data
                      ? (
                        <Skeleton style={{ width: '100%' }}>
                          <TextField
                            fullWidth
                            variant="outlined"
                          />
                        </Skeleton>
                      )
                      : (
                        <TextField
                          onChange={handleInputChange}
                          value={formValues.payrollId}
                          id="payrollId"
                          fullWidth
                          label="ADP ID"
                          variant="outlined"
                        />
                      )
}
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Box px={2}>
                  { binNames && data ? (
                    <Autocomplete
                      autoHighlight
                      autoSelect
                      options={binNames || []}
                      getOptionLabel={(option) => option.value}
                      getOptionSelected={(option, value) => option?.value === value?.value}
                      renderOption={(bin) => (
                        <div style={{ textDecoration: bin.inUse ? 'line-through' : 'none' }}>
                          {bin.label}
                        </div>
                      )}
                      // prop spreading is directly from MUI tsx example
                      // eslint-disable-next-line react/jsx-props-no-spreading
                      renderInput={(params) => <TextField {...params} label="Assigned Bin" variant="outlined" />}
                      value={binNames.find((x) => x?.value === formValues?.assignedLocation)}
                      onChange={(event, value) => {
                        if (value !== null) {
                          setFormValues({ ...formValues, assignedLocation: value?.value || '' });
                        } else {
                          setFormValues({ ...formValues, assignedLocation: '' });
                        }
                      }}
                    />
                  ) : (
                    <Skeleton width="100%" height="56px" variant="rect" />
                  )}
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Box pl={2}>
                  <FormGroup>
                    <FormControlLabel
                      style={{ pointerEvents: 'none' }}
                      control={(
                        userIdUrlParam && !data
                          ? (
                            <Skeleton>
                              <Checkbox />
                            </Skeleton>
                          )
                          : (
                            <Checkbox
                              id="isActive-checkbox"
                              style={{ pointerEvents: 'auto' }}
                              color="primary"
                              name="isActive"
                              checked={checkboxState.isActive}
                              onChange={() => {
                                setCheckboxState({
                                  ...checkboxState,
                                  isActive: !checkboxState.isActive,
                                });
                              }}
                            />
                          )
                  )}
                      label="User is Active"
                    />
                    <FormControlLabel
                      style={{ pointerEvents: 'none' }}
                      control={(
                        userIdUrlParam && !data
                          ? (
                            <Skeleton>
                              <Checkbox />
                            </Skeleton>
                          )
                          : (
                            <Checkbox
                              id="isLocked-checkbox"
                              style={{ pointerEvents: 'auto' }}
                              color="primary"
                              name="isLocked"
                              checked={checkboxState.isLocked}
                              onChange={() => {
                                setCheckboxState({
                                  ...checkboxState,
                                  isLocked: !checkboxState.isLocked,
                                });
                              }}
                            />
                          )
                  )}
                      label="User Is Locked"
                    />
                  </FormGroup>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
        <Grid item xs={12}>
          <Alert severity="info">
            <Typography>{'Admin Employee Level Users are automatically added to all departments.'}</Typography>
            <Typography>{'Manager Employee Level Users are assigned to departments. '}</Typography>
            <Typography>{'Worker Employee Level Users are manually added to a given department or two. '}</Typography>
          </Alert>
        </Grid>
        <Grid item xs={12}>
          <Paper>
            <Grid container spacing={3} alignItems="stretch">
              <Grid item xs={12}>
                <Box pl={2} pt={1}>
                  <Typography variant="h5">{'User Roles'}</Typography>
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Divider />
              </Grid>

              <Box pl={3.5} pb={0} clone>
                <Typography variant="h5">{'Select Facilities'}</Typography>
              </Box>
              <Grid item xs={12} container justifyContent="flex-start">
                {/* selected={!!selectedRepairItem.find((e) => e.id === repairItem.id)}
                    handleClick={() => handleSelectRepairItem(repairItem)} */}
                {facilities
                  ? (facilities.map(
                    (facility) => (
                      <Grid item>
                        <Box m={1} ml={1} my={0.5}>
                          <ChipSelect
                            selected={!!facilitiesSelected.find((f) => f === facility.value)}
                            label={facility.label}
                            handleClick={() => {
                              handleChipFacilitiesSelect(facility, 'facilities');
                            }}
                          />
                        </Box>
                      </Grid>
                    ),
                  ))
                  : (
                    <Grid item>
                      <Skeleton variant="circle">
                        <ChipSelect label="department" selected={false} handleClick={() => {}} />
                      </Skeleton>
                    </Grid>
                  )}
              </Grid>

              <Box pl={3.5} pb={0} clone>
                <Typography variant="h5">{'Select Employee Level'}</Typography>
              </Box>
              <Grid item xs={12} container justifyContent="flex-start">
                {employeeLevels
                  ? (employeeLevels.map(
                    (employeeLevel) => (
                      <Grid item>
                        <Box m={1} ml={1} my={0.5}>
                          <ChipSelect
                            label={employeeLevel.label}
                            selected={!!roleSelected.find((f) => f === employeeLevel.value)}
                            handleClick={() => {
                              handleChipFacilitiesSelect(employeeLevel, 'roles');
                            }}
                          />
                        </Box>
                      </Grid>
                    ),
                  ))
                  : (
                    <Grid item>
                      <Skeleton variant="circle">
                        <ChipSelect label="department" selected={false} handleClick={() => {}} />
                      </Skeleton>
                    </Grid>
                  )}
              </Grid>

              <Box pl={3.5} pb={0} clone>
                <Typography variant="h5">{'Select Associated Department(s)'}</Typography>
              </Box>
              <Grid item xs={12} container justifyContent="flex-start">
                {departments
                  ? (departments.map(
                    (department) => (
                      <Grid item>
                        <Box m={1} ml={1} my={0.5}>
                          <ChipSelect
                            label={department.label}
                            selected={!!departmentSelected.find((d) => d === department.value)}
                            handleClick={() => {
                              handleChipFacilitiesSelect(department, 'departments');
                            }}
                          />
                        </Box>
                      </Grid>
                    ),
                  ))
                  : (
                    <Grid item>
                      <Skeleton variant="circle">
                        <ChipSelect label="department" selected={false} handleClick={() => {}} />
                      </Skeleton>
                    </Grid>
                  )}
              </Grid>

              <Box pl={3.5} pb={0} clone>
                <Typography variant="h5">{'Select Job Functions(s)'}</Typography>
              </Box>
              <Grid item xs={12} container justifyContent="flex-start">
                {stations
                  ? (stations.map(
                    (station) => (
                      <Grid item>
                        <Box m={1} ml={1} my={0.5}>
                          <ChipSelect
                            label={station.label}
                            selected={!!stationsSelected.find((d) => d === station.value)}
                            handleClick={() => {
                              handleChipFacilitiesSelect(station, 'stations');
                            }}
                          />
                        </Box>
                      </Grid>
                    ),
                  ))
                  : (
                    <Grid item>
                      <Skeleton variant="circle">
                        <ChipSelect label="station" selected={false} handleClick={() => {}} />
                      </Skeleton>
                    </Grid>
                  )}
              </Grid>

              <Grid item xs={12} />

            </Grid>
          </Paper>
        </Grid>
        <Grid item xs={12}>
          <Box px={1} pb={1} />
        </Grid>
        <Grid item xs={12}>
          <Paper>
            <Grid container spacing={3} alignItems="stretch">
              <Grid item xs={12}>
                <Box pl={2} pt={1}>
                  <Typography variant="h5" style={{ float: 'left' }}>{'Skill Level(s)'}</Typography>
                  <Button size="large" color="primary" onClick={() => navigate('/skill-level-types')} style={{ float: 'right' }}>
                    {'ADD SKILL LEVEL TYPE'}
                  </Button>
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Divider />
              </Grid>

              <Grid item xs={12}>
                <Box px={2} pb={2}>
                  {
                    (skillLevels && (
                    <Autocomplete
                      key={JSON.stringify(skillLevelsSelected)}
                      defaultValue={skillLevelsSelected}
                      multiple
                      onChange={(event, selectedOptions: ILabelValuePair[]) => {
                        if (selectedOptions) {
                          setSkillLevelsSelected([...selectedOptions.sort((a, b) => -b.label.localeCompare(a.label))]);
                        } else {
                          setSkillLevelsSelected([...[]]);
                        }
                      }}
                      autoHighlight
                      autoSelect
                      options={skillLevels.sort((a, b) => -b.label.localeCompare(a.label))}
                      getOptionLabel={(option) => option.label}
                  // JAG MUI out of the box AutoComplete functionality requires prop spreading.
                  // eslint-disable-next-line react/jsx-props-no-spreading
                      renderInput={(params) => <TextField {...params} label="Search and Add Skill Level" id="skill-level" variant="outlined" />}
                    />
                    )
                    )
                  }
                </Box>
              </Grid>

              <Grid item xs={12} />

            </Grid>
          </Paper>
        </Grid>
        <Grid item xs={12} container spacing={2} direction="row-reverse">
          <Grid item>
            <Button style={{ minWidth: '7.5rem' }} variant="contained" color="primary" disabled={savingUser} onClick={userIdUrlParam ? () => updateEmployee() : () => saveNewEmployee()}>
              {
              // eslint-disable-next-line no-nested-ternary
              savingUser ? <CircularProgress size={25} /> : userIdUrlParam ? 'UPDATE USER' : 'SAVE USER'
              }
            </Button>
          </Grid>
          <Grid item>
            <Button color="primary" onClick={() => navigate('/view-users')}>
              {'BACK TO ALL USERS'}
            </Button>
          </Grid>
        </Grid>
      </Grid>
      <Grid item xs={3} />
      {
        showRepairsModal
        && <UserRepairs open={showRepairsModal} userId={userIdUrlParam || ''} onClose={() => setShowRepairsModal(false)} />
      }
    </Grid>

  );
};

export default UserUpsert;
